import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, FlaskConical, Truck, ShieldCheck, ScanLine, Lock } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";

const IMG = {
  indoor: "/Images/cultivation-canopy.jpg",
  lab: "/Images/extraction-facility.jpg",
  facility: "/Images/investors-hero-facility.jpg",
};

const STAGE_IMAGES = [IMG.indoor, IMG.facility];

const STAGES = [
  { n: "I", icon: Leaf, title: "Controlled-Environment Cultivation",
    body: "An 18-bay, climate-independent indoor environment engineered for total microbial control and zero batch variance. This modular design allows for rapid scaling from 2,000 m² to 4,000 m² flowering canopy.",
    points: ["18-bay modular layout", "Climate-independent · biosecure", "2,000 m² → 4,000 m² canopy"] },
  { n: "II", icon: FlaskConical, title: "The Clean Core",
    body: "A 1,250 m² pharmaceutical post-harvest facility engineered to EU-GMP standards. This facility serves as our industrial engine, designed from day one to handle the increased throughput of our Phase II expansion and our future pathway into extraction and value-added medical products.",
    points: ["1,250 m² · EU-GMP-aligned", "Phase II throughput pre-sized", "Future extraction pathway"] },
  { n: "III", icon: ScanLine, title: "Traceability",
    body: "Seed-to-shipment digital traceability — every batch, every input, every movement.",
    points: ["Batch-level provenance", "Digital chain of custody", "Audit-ready records"] },
  { n: "IV", icon: Lock, title: "Security",
    body: "Tiered physical and digital security designed to meet controlled-substance expectations.",
    points: ["Perimeter security", "Access control", "Cyber & data security"] },
  { n: "V", icon: ShieldCheck, title: "Compliance",
    body: "Regulatory architecture aligned with destination-market frameworks; pathway toward EU-GMP / IMC-GMP certification.",
    points: ["GACP / GMP-aligned", "Export licensing", "Destination-market QA"] },
  { n: "VI", icon: Truck, title: "Export Logistics",
    body: "Validated cold-chain and documentation flows calibrated for licensed European importers.",
    points: ["Validated logistics", "Customs documentation", "Importer integration"] },
];

export default function OurModel() {
  return (
    <>
      <section className="pt-[72px] bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
          <SectionReveal><div className="overline">Our Model</div></SectionReveal>
          <SectionReveal delay={120}>
            <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.04] tracking-editorial max-w-5xl">
              An export-led pharmaceutical platform — built one stage at a time.
            </h1>
          </SectionReveal>
          <SectionReveal delay={240}>
            <p className="mt-8 max-w-2xl text-ink2 text-lg leading-relaxed">
              Maisha Pharma is a vertically integrated platform — from controlled-environment indoor cultivation through GMP-aligned post-harvest and export — engineered to meet the regulatory standards of the markets we serve. Extraction and product manufacturing are planned as later-phase capabilities.
            </p>
          </SectionReveal>
        </div>
      </section>

      <section className="bg-sand/40 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="overline">Pipeline</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-3xl">
              Six operational disciplines. One regulated outcome.
            </h2>
          </SectionReveal>

          <div className="mt-16" data-testid="model-pipeline">
            <div className="space-y-6">
              {STAGES.slice(0, 2).map((s, i) => (
                <SectionReveal key={s.title} delay={i * 100}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 bg-bone">
                    <div className={`relative h-[280px] md:h-[400px] lg:h-auto lg:col-span-6 overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                      <img
                        src={STAGE_IMAGES[i]}
                        alt={s.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.03]"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-forest/15" />
                      <div className="absolute bottom-5 left-5 text-bone/90 text-[10px] tracking-[0.22em] uppercase">Stage {s.n} · {s.title}</div>
                    </div>
                    <div className="lg:col-span-6 p-8 md:p-12 lg:p-14 flex flex-col justify-center">
                      <div className="flex items-start justify-between">
                        <s.icon size={20} strokeWidth={1.5} className="text-copper" />
                        <div className="font-mono text-[11px] tracking-[0.2em] text-copper">Stage {s.n}</div>
                      </div>
                      <h3 className="mt-8 font-display text-2xl md:text-3xl tracking-tight">{s.title}</h3>
                      <p className="mt-4 text-sm md:text-base text-ink2 leading-relaxed max-w-md">{s.body}</p>
                      <ul className="mt-6 space-y-2">
                        {s.points.map((p) => (
                          <li key={p} className="flex items-center gap-3 text-sm text-ink">
                            <span className="h-px w-5 bg-copper" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              {STAGES.slice(2).map((s, i) => (
                <SectionReveal key={s.title} delay={i * 80} className="h-full">
                  <div className="bg-bone p-8 md:p-10 h-full">
                    <div className="flex items-start justify-between">
                      <s.icon size={20} strokeWidth={1.5} className="text-copper" />
                      <div className="font-mono text-[11px] tracking-[0.2em] text-copper">Stage {s.n}</div>
                    </div>
                    <h3 className="mt-8 font-display text-2xl tracking-tight">{s.title}</h3>
                    <p className="mt-3 text-sm text-ink2 leading-relaxed max-w-md">{s.body}</p>
                    <ul className="mt-6 space-y-2">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-sm text-ink">
                          <span className="h-px w-5 bg-copper" />{p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-0 md:px-12 py-0 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-16 items-stretch">
            <SectionReveal className="lg:col-span-7">
              <div className="relative h-[420px] md:h-[600px] overflow-hidden">
                <img src={IMG.lab} alt="Pharmaceutical extraction machinery" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.03]" loading="lazy" />
                <div className="absolute inset-0 bg-forest/25" />
                <div className="absolute bottom-6 left-6 text-bone/90 text-[11px] tracking-[0.22em] uppercase">FIG.04 · Future Extraction Capability</div>
              </div>
            </SectionReveal>
            <div className="lg:col-span-5 px-6 md:px-0 py-16 lg:py-0 flex flex-col justify-center">
              <SectionReveal>
                <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Regulated by Design</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial text-bone">
                  We do not retrofit compliance. We start with it.
                </h2>
              </SectionReveal>
              <SectionReveal delay={140}>
                <p className="mt-6 text-bone/65 max-w-md leading-relaxed">
                  Site design, equipment selection, software systems, and SOPs are scoped against the regulatory frameworks of our destination markets — before the first plant is in the ground. Extraction and product manufacturing are planned as later-phase capabilities, building on a proven cultivation and post-harvest foundation.
                </p>
              </SectionReveal>
              <SectionReveal delay={260}>
                <Link to="/contact" data-testid="model-cta-contact" className="mt-10 inline-flex items-center gap-2 bg-bone text-ink px-7 py-4 text-sm tracking-wide hover:bg-sand transition-colors w-fit">
                  Discuss the platform <ArrowUpRight size={16} />
                </Link>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
